import { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Calculator, TrendingUp, AlertCircle, CheckCircle } from 'lucide-react';
import { formatCurrency, formatHumanReadable } from '@/utils/currency';
import { toast } from 'sonner';

function useIsMobile() {
  const [m, setM] = useState(false);
  useEffect(() => { const c = () => setM(window.innerWidth < 768); c(); window.addEventListener('resize', c); return () => window.removeEventListener('resize', c); }, []);
  return m;
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function RetirementCalculator({ user, onLogout }) {
  const [formData, setFormData] = useState({
    current_age: '',
    retirement_age: 65,
    current_savings: '',
    monthly_contribution: '',
    expected_return: 7,
    inflation_rate: 3,
    life_expectancy: 85,
    monthly_expense: ''
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const isMobile = useIsMobile();

  const handleCalculate = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/analytics/retirement-calculator`,
        {
          current_age: parseInt(formData.current_age),
          retirement_age: parseInt(formData.retirement_age),
          current_savings: parseFloat(formData.current_savings),
          monthly_contribution: parseFloat(formData.monthly_contribution),
          expected_return: parseFloat(formData.expected_return),
          inflation_rate: parseFloat(formData.inflation_rate),
          life_expectancy: parseInt(formData.life_expectancy),
          monthly_expense: parseFloat(formData.monthly_expense)
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResult(response.data);
      toast.success('Retirement plan calculated!');
    } catch (error) {
      toast.error('Error calculating retirement plan');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen p-4 lg:p-8" data-testid="retirement-calculator-page">
      <div className="max-w-5xl mx-auto relative">
        <Navigation user={user} onLogout={onLogout} currentPage="/retirement-calculator" />
        {!isMobile && (
          <div className="pointer-events-none fixed inset-0 z-0" aria-hidden="true">
            <motion.div className="absolute w-[400px] h-[400px] rounded-full bg-gradient-to-r from-indigo-600/[0.06] to-purple-500/[0.06] blur-3xl" animate={{ x: [0, -20, 0], y: [0, 25, 0] }} transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }} style={{ bottom: '10%', right: '5%' }} />
          </div>
        )}

        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <motion.div className="w-2 h-8 rounded-full bg-gradient-to-b from-indigo-500 to-purple-400" initial={{ scaleY: 0 }} animate={{ scaleY: 1 }} transition={{ duration: 0.4, delay: 0.2 }} />
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-800 dark:text-gray-100">
              <span className="bg-gradient-to-r from-indigo-500 to-purple-400 bg-clip-text text-transparent">Retirement</span> Calculator
            </h1>
          </div>
          <p className="text-gray-600 dark:text-gray-400 ml-5 text-sm">Plan your financial future and retirement goals</p>
        </motion.div>

        <motion.div className="grid grid-cols-1 lg:grid-cols-2 gap-6" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.1 }}>
          {/* Input Form */}
          <Card className="p-6 border-gray-200 dark:border-gray-800/60">
            <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-6">Your Information</h2>
            <form onSubmit={handleCalculate} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="dark:text-gray-300">Current Age</Label>
                  <Input
                    type="number"
                    value={formData.current_age}
                    onChange={(e) => setFormData({ ...formData, current_age: e.target.value })}
                    required
                    placeholder="30"
                    className="dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>
                <div>
                  <Label className="dark:text-gray-300">Retirement Age</Label>
                  <Input
                    type="number"
                    value={formData.retirement_age}
                    onChange={(e) => setFormData({ ...formData, retirement_age: e.target.value })}
                    required
                    placeholder="65"
                    className="dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>
              </div>

              <div>
                <Label className="dark:text-gray-300">Current Savings</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.current_savings}
                  onChange={(e) => setFormData({ ...formData, current_savings: e.target.value })}
                  required
                  placeholder="50000"
                  className="dark:bg-gray-700 dark:text-gray-100"
                />
              </div>

              <div>
                <Label className="dark:text-gray-300">Monthly Contribution</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.monthly_contribution}
                  onChange={(e) => setFormData({ ...formData, monthly_contribution: e.target.value })}
                  required
                  placeholder="1000"
                  className="dark:bg-gray-700 dark:text-gray-100"
                />
              </div>

              <div>
                <Label className="dark:text-gray-300">Expected Monthly Expense (Today's Value)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.monthly_expense}
                  onChange={(e) => setFormData({ ...formData, monthly_expense: e.target.value })}
                  required
                  placeholder="3000"
                  className="dark:bg-gray-700 dark:text-gray-100"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="dark:text-gray-300">Expected Return (%)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.expected_return}
                    onChange={(e) => setFormData({ ...formData, expected_return: e.target.value })}
                    required
                    placeholder="7"
                    className="dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>
                <div>
                  <Label className="dark:text-gray-300">Inflation Rate (%)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.inflation_rate}
                    onChange={(e) => setFormData({ ...formData, inflation_rate: e.target.value })}
                    required
                    placeholder="3"
                    className="dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>
              </div>

              <div>
                <Label className="dark:text-gray-300">Life Expectancy</Label>
                <Input
                  type="number"
                  value={formData.life_expectancy}
                  onChange={(e) => setFormData({ ...formData, life_expectancy: e.target.value })}
                  required
                  placeholder="85"
                  className="dark:bg-gray-700 dark:text-gray-100"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white"
              >
                {loading ? 'Calculating...' : 'Calculate Retirement Plan'}
              </Button>
            </form>
          </Card>

          {/* Results */}
          <div className="space-y-6">
            {result ? (
              <>
                <Card className={`glass p-6 border-gray-200 dark:border-gray-800/60 ${result.is_on_track ? 'border-2 border-green-400 dark:border-green-600' : 'border-2 border-amber-400 dark:border-amber-600'}`}>
                  <div className="flex items-center space-x-3 mb-4">
                    {result.is_on_track ? (
                      <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
                    ) : (
                      <AlertCircle className="w-8 h-8 text-amber-600 dark:text-amber-400" />
                    )}
                    <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
                      {result.is_on_track ? 'You\'re On Track!' : 'Action Needed'}
                    </h2>
                  </div>
                  <p className="text-gray-600 dark:text-gray-300">
                    {result.is_on_track
                      ? 'Your current savings plan will meet your retirement goals.'
                      : 'You need to increase your savings to meet your retirement goals.'}
                  </p>
                </Card>

                <Card className="glass p-6 border-gray-200 dark:border-gray-800/60">
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">Retirement Corpus</h3>
                  <div className="space-y-4">
                    <div className="bg-emerald-50 dark:bg-emerald-900/20 p-4 rounded-xl">
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Expected Corpus at Retirement</p>
                      <p className="text-3xl font-bold text-emerald-600 dark:text-emerald-400">
                        {formatCurrency(result.total_corpus_at_retirement, 'INR')}
                      </p>
                      <p className="text-sm text-emerald-500 dark:text-emerald-300 mt-1">
                        ({formatHumanReadable(result.total_corpus_at_retirement, 'INR')})
                      </p>
                    </div>
                    <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-xl">
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Required Corpus</p>
                      <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                        {formatCurrency(result.required_corpus, 'INR')}
                      </p>
                      <p className="text-xs text-blue-500 dark:text-blue-300 mt-1">
                        ({formatHumanReadable(result.required_corpus, 'INR')})
                      </p>
                    </div>
                    {!result.is_on_track && (
                      <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-xl">
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Shortfall</p>
                        <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">
                          {formatCurrency(result.shortfall, 'INR')}
                        </p>
                        <p className="text-xs text-amber-500 dark:text-amber-300 mt-1">
                          ({formatHumanReadable(result.shortfall, 'INR')})
                        </p>
                      </div>
                    )}
                  </div>
                </Card>

                <Card className="glass p-6 border-gray-200 dark:border-gray-800/60">
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">Monthly Breakdown</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center p-3 bg-white dark:bg-gray-700 rounded-lg">
                      <span className="text-gray-600 dark:text-gray-300">Monthly Income in Retirement</span>
                      <div className="text-right">
                        <span className="font-semibold text-gray-800 dark:text-gray-200">
                          {formatCurrency(result.monthly_income_in_retirement, 'INR')}
                        </span>
                        <p className="text-xs text-gray-500">({formatHumanReadable(result.monthly_income_in_retirement, 'INR')})</p>
                      </div>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-white dark:bg-gray-700 rounded-lg">
                      <span className="text-gray-600 dark:text-gray-300">Adjusted Monthly Expense</span>
                      <div className="text-right">
                        <span className="font-semibold text-gray-800 dark:text-gray-200">
                          {formatCurrency(result.adjusted_monthly_expense, 'INR')}
                        </span>
                        <p className="text-xs text-gray-500">({formatHumanReadable(result.adjusted_monthly_expense, 'INR')})</p>
                      </div>
                    </div>
                    {!result.is_on_track && (
                      <div className="flex justify-between items-center p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                        <span className="text-amber-800 dark:text-amber-300 font-medium">Additional Monthly Savings Needed</span>
                        <div className="text-right">
                          <span className="font-bold text-amber-600 dark:text-amber-400">
                            {formatCurrency(result.additional_monthly_savings_needed, 'INR')}
                          </span>
                          <p className="text-xs text-amber-500">({formatHumanReadable(result.additional_monthly_savings_needed, 'INR')})</p>
                        </div>
                      </div>
                    )}
                  </div>
                </Card>
              </>
            ) : (
              <Card className="glass p-12 text-center border-gray-200 dark:border-gray-800/60">
                <TrendingUp className="w-16 h-16 text-indigo-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-2">Enter Your Details</h3>
                <p className="text-gray-600 dark:text-gray-400">Fill in the form to calculate your retirement plan</p>
              </Card>
            )}
          </div>
        </motion.div>
        <Footer />
      </div>
    </div>
  );
}